import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { therapeuticAreaRelativesProducts } from 'basics/constants/common.constants';
import { BiogenProduct } from 'basics/enums/biogen.enums';
import { SelectOption } from 'basics/options/options.types';
import { CustomerInitiative, StrategicImperative } from 'generated/maya.types';
import { CiStatus } from 'modules/maya/basics/enums/maya.enums';
import { ciToBE } from 'modules/maya/basics/transformers/CustomerInitiative.transformer';
import useCustomerInitiativeFormConfig from 'modules/maya/components/CustomerInitiativeForm/CustomerInitiativeForm.config';
import {
  CiFormCanceltCallbackType,
  CiFormSubmitCallbackType,
} from 'modules/maya/components/CustomerInitiativeForm/CustomerInitiativeForm.types';
import useGetStrategicImperativeByIdQuery from 'modules/maya/graphql/queries/getStrategicImperativeById';
import { useYodaCreateForm } from 'services/yodaForm';
import { ButtonType } from 'yoda-ui/Button/Button.types';

const useCustomerInitiativeCreate = (
  parentStrategicImperative: StrategicImperative | undefined,
  customerInitiative: CustomerInitiative | undefined,
  submitCallback: CiFormSubmitCallbackType,
  cancelCallback: CiFormCanceltCallbackType,
) => {
  const { t } = useTranslation();

  const { getValues, providerFields, resetForm, useWatchForm, setValue, useWatchField, setFieldShowError } = useYodaCreateForm();
  const { isValid } = useWatchForm();

  const [loading, setLoading] = useState(true);
  const [siLoading, setSiLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSi, setselectedSi] = useState<string | null>(null);
  const [selectedTA, setSelectedTA] = useState<string | null>(null);

  const { customerInitiativeFormConfig } = useCustomerInitiativeFormConfig(
    customerInitiative,
    parentStrategicImperative,
    selectedTA,
  );

  if (customerInitiative) {
    const fieldsName = Object.keys(customerInitiativeFormConfig);
    setFieldShowError(true, fieldsName);
  }

  useEffect(() => {
    if (customerInitiativeFormConfig?.strategicImperative?.options.length) {
      setLoading(false);
    }
  }, [customerInitiativeFormConfig?.strategicImperative?.options, setLoading]);

  const currentStrategicImperative = useGetStrategicImperativeByIdQuery(selectedSi).data;
  useEffect(() => {
    if (currentStrategicImperative) {
      setValue(customerInitiativeFormConfig.therapeuticArea.name, currentStrategicImperative?.therapeuticArea);
      setSiLoading(false);
    }
  }, [currentStrategicImperative, customerInitiativeFormConfig.therapeuticArea.name, setValue]);

  const therapeuticAreaKey = useWatchField(customerInitiativeFormConfig.therapeuticArea.name)?.value;
  useEffect(() => {
    setSelectedTA(therapeuticAreaKey);
  }, [therapeuticAreaKey]);

  const handleChangeStrategicImperative = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption) => {
    setSiLoading(!!value);
    setselectedSi(value?.id as string);
  }, [setselectedSi]);

  const handleChangeTA = useCallback((event: SyntheticEvent<Element, Event>, toClearTAField: SelectOption | null) => {
    if (toClearTAField === null) {
      setValue(customerInitiativeFormConfig.product.name, []);
    }
  }, [setValue, customerInitiativeFormConfig]);

  const handleChangeProduct = useCallback((event: SyntheticEvent<Element, Event>, selectedProduct: SelectOption[]) => {
    if (selectedProduct && selectedProduct.length > 0) {
      const therapeuticAreaValue = Object.keys(therapeuticAreaRelativesProducts)
        .find((key: string) => therapeuticAreaRelativesProducts[key as keyof typeof therapeuticAreaRelativesProducts]
          ?.includes(selectedProduct[0].id as BiogenProduct));
      setValue(customerInitiativeFormConfig.therapeuticArea.name, therapeuticAreaValue);
    }
  }, [setValue, customerInitiativeFormConfig]);

  const handleCancelCustomerInitiativeForm = () => {
    resetForm();
    cancelCallback();
  };

  const handleSaveCustomerInitiativeForm = async () => {
    setIsSubmitting(true);
    const formValues = getValues();
    const formattedCI = ciToBE(formValues);

    await submitCallback(formattedCI);

    setIsSubmitting(false);
  };

  const cancelButtonConfig = {
    buttonProps: {
      onClick: handleCancelCustomerInitiativeForm,
      buttonType: ButtonType.secondary,
    },
    label: t('actions_cancel_label'),
  };

  const submitButtonConfig = {
    buttonProps: {
      onClick: handleSaveCustomerInitiativeForm,
      buttonType: ButtonType.primary,
      disabled: customerInitiative?.status === CiStatus.ACTIVE || !isValid || isSubmitting,
    },
    label: t('actions_save_label'),
    loading: isSubmitting,
  };

  return {
    cancelButtonConfig,
    customerInitiativeFormConfig,
    handleChangeProduct,
    handleChangeStrategicImperative,
    handleChangeTA,
    loading,
    providerFields,
    siLoading,
    submitButtonConfig,
  };
};

export default useCustomerInitiativeCreate;
